import { useQueryClient } from "@tanstack/react-query";
import { IANAZone } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useModals } from "../../../../context/ModalContext";
import { gateway } from "../../../../gateway";
import {
  resetJobCreateStepper,
  setCreditedDepositAmount,
  setDescription,
  setLastCreatedJobId,
  setNotes,
  setScratchCreateAutoFlow,
} from "../../../../redux/features/jobCreateSlice";
import { quoteCreateActions } from "../../../../redux/features/quotes/quote-create-advanced";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { usePatchJob } from "../../../clients/hooks/usePatchJob";
import { useSnackbar } from "../../../common/snackbar/useSnackbar";
import type { Job } from "../../../jobs/interfaces/job.interfaces";
import { JobDetails } from "../../../jobs/jobs-details/JobDetails";
import { EMode } from "../../../shared/components/features/event-scheduler/enums/event.enums";
import { ScopeOfWorkHandle } from "../../../shared/components/features/scope-of-work/controller/scope-of-work.handle";
import { useCompanyInfo } from "../../../shared/hooks/useCompany";
import { getTimelineClickedTime } from "../../config/timeline.constants";
import { CALENDAR_SELECTED_INSTANT_KEY } from "../../jobs-calendar/utils/companyWallTime";
import { VisitStepper } from "../../JobVisits/job-visit-create/VisitStepper";
import { buildJobPatchPayload } from "../helper/buildJobPatchPayload";
import { buildScopeOfWorkPatchPayload } from "../helper/buildScopeOfWorkPatchPayload";
import { formatedForCreatedJobWithOutQuoteAdvanced } from "../helper/formatedDataForJobAdvanced";
import { formatedForCreatedJobWithOutQuote } from "../helper/formatedForCreatedJobWithOutQuote";
import { formatStaffForJobPayload } from "../helper/formatedStaffForPayload";
import { withResolvedCoverage } from "../helper/resolvePreferredCoverageArea";
import { applyTimeZoneToEventBase, attachEventMeta, transformEventBase } from "../helper/transforEventScheduler";
import { resolveJobEventSchedulerPayload } from "../validators/resolveJobEventScheduler";
import { useCreateJobWithoutQuote, useEventScheduler } from "./useCreateJobWithoutQuote";
import { useJobCreate } from "./useJobCreate";

interface IUseJobsCreateController {
  modalKey?: string | null;
  isUpdateJob?: boolean;
  jobId?: string | number | null;
}

export function useJobsCreateController({ modalKey = "", isUpdateJob = false, jobId = null }: IUseJobsCreateController = {}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const state = useSelector((reduxState: any) => reduxState.jobCreateStepper);
  const companyData = useCompanyInfo();

  const { company, uid: accountUid } = useAppSelector((reduxState) => reduxState.userState);

  const scopeOfWorkRef = useRef<ScopeOfWorkHandle>(null);
  const unconfirmedScheduleJobRef = useRef<number | string | null>(null);
  const [unconfirmedScheduleJobId, setUnconfirmedScheduleJobId] = useState<number | string | null>(null);
  const pendingScheduleRef = useRef<{ jobId: number | string; payload: Record<string, any>; fromQuote: boolean } | null>(null);
  const [isRetryingSchedule, setIsRetryingSchedule] = useState(false);
  const [scheduleRecovered, setScheduleRecovered] = useState(false);
  const [reviewedPendingJob, setReviewedPendingJob] = useState(false);
  const requestKeyRef = useRef<string | null>(null);
  const pendingStoreKey = company?.id && accountUid ? `flownect:job-create-pending:${company.id}:${accountUid}` : null;

  useEffect(() => {
    if (!pendingStoreKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(pendingStoreKey) || "null");
      if (!saved?.requestKey) return;
      requestKeyRef.current = saved.requestKey;
      if (saved.jobId) {
        unconfirmedScheduleJobRef.current = saved.jobId;
        setUnconfirmedScheduleJobId(saved.jobId);
        const schedule = JSON.parse(localStorage.getItem(`${pendingStoreKey}:schedule`) || "null");
        if (schedule?.jobId === saved.jobId) pendingScheduleRef.current = schedule;
      }
    } catch {
      // A damaged local draft must not be treated as a confirmed schedule.
    }
  }, [pendingStoreKey]);

  const savePendingRequest = (jobId?: number | string) => {
    if (pendingStoreKey && requestKeyRef.current) {
      try {
        localStorage.setItem(pendingStoreKey, JSON.stringify({ requestKey: requestKeyRef.current, jobId: jobId || null }));
      } catch (error) {
        console.error("Unable to save pending job request locally:", error);
      }
    }
  };

  const clearPendingRequest = () => {
    if (pendingStoreKey) {
      try {
        localStorage.removeItem(pendingStoreKey);
        localStorage.removeItem(`${pendingStoreKey}:schedule`);
      } catch (error) {
        console.error("Unable to clear pending job request locally:", error);
      }
    }
    requestKeyRef.current = null;
  };

  const { createJobWithQuote, createJobEvent, loading } = useJobCreate();

  const { createJob, isCreating } = useCreateJobWithoutQuote();

  const { createJob: sendToEventScheduler } = useEventScheduler();

  const { mutateAsync: patchJob } = usePatchJob();

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const { closeByKey, openModal } = useModals();

  const selectedServiceEntry = Array.isArray(state.selectedService) ? state.selectedService[0] : state.selectedService;

  const basicScopeItem = Array.isArray(state.scopeOfWork) ? state.scopeOfWork[0] : state.scopeOfWork;

  const selectedServiceItem =
    selectedServiceEntry?.service && typeof selectedServiceEntry.service === "object" ? selectedServiceEntry.service : selectedServiceEntry;

  const useServiceNameOverride = Boolean(
    selectedServiceItem?.useServiceNameOverride ?? selectedServiceEntry?.useServiceNameOverride ?? basicScopeItem?.useServiceNameOverride,
  );

  const serviceNameOverride = useServiceNameOverride
    ? (selectedServiceItem?.serviceNameOverride ?? selectedServiceEntry?.serviceNameOverride ?? basicScopeItem?.serviceNameOverride ?? "").trim() ||
      null
    : null;

  const currentMode =
    state.eventScheduler?.eventPreferences?.eventConfig?.mode ||
    selectedServiceItem?.eventPreferences?.eventConfig?.mode ||
    selectedServiceItem?.service?.eventPreferences?.eventConfig?.mode;

  const scheduleLater = currentMode === EMode.CONFIGURE_LATER || currentMode === "CONFIGURE_LATER";

  const refetchTodayJobs = async () => {
    await queryClient.refetchQueries({
      queryKey: ["today-jobs"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["jobs"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["job"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["job-events"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["widget-job-events"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["quote-deposit-available"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["scopeOfWork"],
    });
  };

  const updateExistingJob = async (currentJobId: string | number, createPayload: Record<string, unknown>) => {
    const patchPayload = buildJobPatchPayload(createPayload);

    const scopePayload = createPayload.scopeOfWork;

    const jobPatchBody: Record<string, unknown> = {
      ...patchPayload,
    };

    if (scopePayload && typeof scopePayload === "object") {
      jobPatchBody.scopeOfWork = buildScopeOfWorkPatchPayload(
        scopePayload as Record<string, unknown>,
        typeof createPayload.type === "string" ? createPayload.type : null,
      );

      if (createPayload.type === "basic" || createPayload.type === "advanced") {
        jobPatchBody.type = createPayload.type;
      }
    }

    if (Object.keys(jobPatchBody).length === 0) {
      await queryClient.invalidateQueries({
        queryKey: ["job", String(currentJobId)],
      });

      return {
        data: {
          id: currentJobId,
        },
      };
    }

    const response = await patchJob({
      jobId: currentJobId,
      payload: jobPatchBody,
    });

    await queryClient.invalidateQueries({
      queryKey: ["job", String(currentJobId)],
    });

    await queryClient.invalidateQueries({
      queryKey: ["scopeOfWork"],
    });

    return response;
  };

  const handleSuccessUI = async (
    id?: number | string,
    options?: {
      isFromScratch?: boolean;
      isVisit?: boolean;
      hasClient?: boolean;
    },
  ) => {
    showSnackbar(isUpdateJob ? "Job updated successfully!" : "Job created successfully!", "success");

    localStorage.removeItem("jobCreateSelectedDate");
    localStorage.removeItem(CALENDAR_SELECTED_INSTANT_KEY);

    if (isUpdateJob && jobId) {
      closeByKey(`Job-Edit-${jobId}`);

      dispatch(resetJobCreateStepper());

      dispatch(quoteCreateActions.setSectorWorkflowAlias(""));

      void queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });

      void queryClient.invalidateQueries({
        queryKey: ["job", String(jobId)],
      });

      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["jobs"],
    });

    const isTimelineFlow = Boolean(getTimelineClickedTime());

    const closeDelayMs = options?.isFromScratch ? 300 : 1000;

    setTimeout(async () => {
      await closeByKey(`Dashboard-JobsAdd-Business${modalKey ? `-${modalKey}` : ""}`);

      await closeByKey(`Dashboard-JobsAdd-Business-Without-Quote${modalKey ? `-${modalKey}` : ""}`);

      await closeByKey("Timeline-JobsAdd-Business-Without-Quote");

      if (isTimelineFlow && id) return;

      if (!modalKey) {
        openModal("Dashboard-Jobs-Business");
      }

      if (id && options?.isFromScratch) {
        const jobModalKey = `JobCard-${id}`;
        const eventModalKey = `Job-Open-New-Event-${id}`;
        const shouldOpenVisit = Boolean(options?.isVisit && options?.hasClient);
        const job = { id, isVisit: shouldOpenVisit } as Job;

        openModal(jobModalKey, <JobDetails job={job} backModalKey={jobModalKey} />);

        if (shouldOpenVisit) {
          setTimeout(() => {
            openModal(eventModalKey, <VisitStepper jobId={Number(id)} closeModalKey={eventModalKey} />);
          }, 350);
        }
      }

      dispatch(resetJobCreateStepper());

      dispatch(quoteCreateActions.setSectorWorkflowAlias(""));
    }, closeDelayMs);
  };

  const handleCreateJob = async () => {
    if (unconfirmedScheduleJobRef.current) {
      showSnackbar(`Job #${unconfirmedScheduleJobRef.current} already exists. Review its schedule before creating another job.`, "warning");
      return;
    }
    const refreshedCompany = await companyData.refetch();

    const companyTimeZone = refreshedCompany.data?.timeZone ?? company?.timeZone;
    let schedulerRegistrationJobId: number | string | null = null;
    let schedulerRegistrationPending = false;

    try {
      if (!isUpdateJob) {
        requestKeyRef.current ||= crypto.randomUUID();
        savePendingRequest();
      }
      /*
       * Validate pricing when creating without quote.
       */
      if (state.quote === null) {
        if (state.quoteMode === "advanced") {
          const sector = state.scopeOfWork ?? {};

          const costOverride = sector?.costOverride;

          const overrideAmount =
            costOverride == null
              ? Number.NaN
              : typeof costOverride === "object"
                ? Number(
                    (
                      costOverride as {
                        price?: unknown;
                      }
                    ).price ??
                      (
                        costOverride as {
                          value?: unknown;
                        }
                      ).value ??
                      (
                        costOverride as {
                          amount?: unknown;
                        }
                      ).amount ??
                      0,
                  )
                : Number(costOverride);

          if (!Number.isFinite(overrideAmount) || overrideAmount <= 0) {
            showSnackbar("Pricing override is required before creating an advanced job.", "error");

            return;
          }
        } else {
          const scopeItems = state.scopeOfWork;

          const firstScopeItem = Array.isArray(scopeItems) && scopeItems.length > 0 ? scopeItems[0] : {};

          const priceRaw = (
            firstScopeItem as {
              price?: string | number;
            }
          ).price;

          const priceAmount = priceRaw === "" || priceRaw == null ? Number.NaN : Number(priceRaw);

          if (!Number.isFinite(priceAmount) || priceAmount <= 0) {
            showSnackbar("Pricing override is required before creating this job.", "error");

            return;
          }
        }
      }

      const fallbackConfigureLaterEvent = {
        mode: EMode.CONFIGURE_LATER,
      };

      const eventSource = state.quote === null ? state.eventScheduler : resolveJobEventSchedulerPayload(state);

      const transformedEvent = transformEventBase(eventSource) ?? fallbackConfigureLaterEvent;

      const configureLater = transformedEvent.mode === EMode.CONFIGURE_LATER || transformedEvent.mode === "CONFIGURE_LATER";

      const scheduleLaterEffective = scheduleLater || configureLater;

      const isVisit = scheduleLaterEffective;

      const canPostToEventScheduler =
        Boolean(transformedEvent) &&
        !configureLater &&
        (transformedEvent.mode === EMode.SINGLE_EXECUTION || transformedEvent.mode === EMode.REPETITIVE);

      if (!isUpdateJob && canPostToEventScheduler && !IANAZone.isValidZone(companyTimeZone)) {
        throw new Error("Set a valid company time zone before scheduling this job.");
      }

      // Resolve ambiguous or nonexistent company times before creating the job.
      // A rejected schedule must not leave a new job without its intended visit.
      const eventWithTimeZone = !isVisit && canPostToEventScheduler && !isUpdateJob
        ? applyTimeZoneToEventBase(transformedEvent, companyTimeZone,
            transformedEvent.mode === EMode.SINGLE_EXECUTION
              ? localStorage.getItem(CALENDAR_SELECTED_INSTANT_KEY) : null)
        : null;

      let jobResponse: any;

      /*
       * Create or update a job without quote.
       */
      if (state.quote === null) {
        let payloadBase: any = {};
        const clientForPayload = withResolvedCoverage(state.client);
        const stateForPayload = clientForPayload === state.client ? state : { ...state, client: clientForPayload };

        if (state.quoteMode === "advanced") {
          payloadBase = formatedForCreatedJobWithOutQuoteAdvanced(stateForPayload, Number(company.id), transformedEvent, state.quoteMode);
        } else {
          payloadBase = formatedForCreatedJobWithOutQuote(stateForPayload, Number(company.id), transformedEvent, state.quoteMode);

          payloadBase.scopeOfWork = {
            ...payloadBase.scopeOfWork,
            serviceNameOverride,
          };
        }

        const payload: any = {
          ...payloadBase,
          ...(isVisit && {
            isVisit: true,
          }),
          type: state.quoteMode,
          ...(!isUpdateJob && { clientRequestKey: requestKeyRef.current }),
        };

        payload.clientUid = typeof clientForPayload === "string" ? clientForPayload : clientForPayload?.uid;

        payload.coverageAreaId = typeof clientForPayload === "object" ? clientForPayload?.coverageArea?.id : undefined;

        const finalAlias = state.alias?.trim();

        if (finalAlias) {
          payload.alias = finalAlias;
        } else {
          delete payload.alias;
        }

        const finalDescription = state.description;

        if (finalDescription?.trim()) {
          payload.description = finalDescription.trim();
        } else {
          delete payload.description;
        }

        const draftNotes = Array.isArray(state.notes) ? state.notes : [];
        const notesPayload = draftNotes
          .filter((note: any) => note?.content?.trim())
          .map((note: any) => ({
            content: note.content.trim(),
            type: note.type || "job",
            pinned: Boolean(note.pinned),
          }));

        if (!isUpdateJob && notesPayload.length > 0) {
          payload.notes = notesPayload;
        } else {
          delete payload.notes;
        }

        if (isUpdateJob && jobId) {
          jobResponse = await updateExistingJob(jobId, payload);
        } else {
          jobResponse = await createJob(payload);
        }
      } else {
        /*
         * Create or update a job from an existing quote.
         */
        let creditedDepositAmount = state.creditedDepositAmount;

        if (state.quote?.id != null && creditedDepositAmount != null && Number(creditedDepositAmount) > 0) {
          try {
            const response = await gateway.get(`/quotes/${state.quote.id}/deposit-available`);

            const available = Number((response.data as any)?.data?.available ?? 0);

            const cappedDeposit = Math.min(Number(creditedDepositAmount), available);

            creditedDepositAmount = cappedDeposit > 0 ? cappedDeposit : null;

            dispatch(setCreditedDepositAmount(creditedDepositAmount));
          } catch {
            creditedDepositAmount = null;

            dispatch(setCreditedDepositAmount(null));
          }
        }

        const workforce = formatStaffForJobPayload(state.workforce);

        const selectedClient = state.client;

        const resolvedClientUid = typeof selectedClient === "string" ? selectedClient : selectedClient?.uid || state.quote?.clientUid;

        const resolvedCoverageAreaId =
          selectedClient && typeof selectedClient === "object"
            ? selectedClient?.coverageArea?.id ||
              selectedClient?.coverageAreaId ||
              selectedClient?.coverageAreas?.find((area: any) => area?.isDefault)?.id ||
              selectedClient?.coverageAreas?.[0]?.id ||
              state.quote?.coverageAreaId
            : state.quote?.coverageAreaId;

        const selectedServiceUid =
          selectedServiceItem?.serviceUid || selectedServiceItem?.service?.serviceUid || state.quote?.services?.[0]?.serviceUid;

        const jobPayload: any = {
          quoteId: Number(state.quote?.id),

          serviceUid: selectedServiceUid,

          workforce,

          eventConfig: transformedEvent,

          type: state.quote?.type,

          clientUid: resolvedClientUid,

          coverageAreaId: resolvedCoverageAreaId,

          ...(isVisit && {
            isVisit: true,
          }),

          ...(creditedDepositAmount != null &&
            creditedDepositAmount > 0 && {
              creditedDepositAmount,
            }),

          ...(state.scopeOfWork?.costOverride || state.scopeOfWork?.serviceNameOverride?.trim()
            ? {
                scopeOfWork: {
                  ...(state.scopeOfWork?.costOverride
                    ? {
                        costOverride: state.scopeOfWork.costOverride,
                      }
                    : {}),

                  serviceNameOverride: state.scopeOfWork?.serviceNameOverride?.trim() || null,
                },
              }
            : {}),
        };

        const finalAlias = state.alias?.trim() || state.quote?.alias || state.quote?.title || state.quote?.name || "";

        if (finalAlias.trim()) {
          jobPayload.alias = finalAlias.trim();
        } else {
          delete jobPayload.alias;
        }

        const finalDescription = state.description;

        if (finalDescription?.trim()) {
          jobPayload.description = finalDescription.trim();
        } else {
          delete jobPayload.description;
        }

        const draftNotes = Array.isArray(state.notes) ? state.notes : [];
        const notesPayload = draftNotes
          .filter((note: any) => note?.content?.trim())
          .map((note: any) => ({
            content: note.content.trim(),
            type: note.type || "job",
            pinned: Boolean(note.pinned),
          }));

        if (!isUpdateJob && notesPayload.length > 0) {
          jobPayload.notes = notesPayload;
        } else {
          delete jobPayload.notes;
        }

        if (isUpdateJob && jobId) {
          jobResponse = await updateExistingJob(jobId, jobPayload);
        } else {
          jobPayload.clientRequestKey = requestKeyRef.current;
          jobResponse = await createJobWithQuote(jobPayload);
        }
      }

      const targetJobId = jobResponse?.data?.id || jobId;

      if (targetJobId) {
        dispatch(setLastCreatedJobId(targetJobId));
        if (!isUpdateJob) savePendingRequest(targetJobId);
      }

      const isFromScratch = state.quote === null;

      const hasClient = !state.skipClientSelection && Boolean(typeof state.client === "string" ? state.client : state.client?.uid);

      if (!isUpdateJob && isFromScratch && targetJobId && !getTimelineClickedTime()) {
        dispatch(
          setScratchCreateAutoFlow({
            jobId: targetJobId,
            openCreateEvent: isVisit && hasClient,
          }),
        );
      }

      let createdEventResponse: any = null;

      if (!isVisit && targetJobId && canPostToEventScheduler && !isUpdateJob) {
        if (eventWithTimeZone) {
          schedulerRegistrationJobId = targetJobId;
          schedulerRegistrationPending = true;
          const schedulerPayload = attachEventMeta(eventWithTimeZone, "Jobs", targetJobId);
          const scheduleCompanyId = Number(refreshedCompany.data?.id ?? company?.id);
          if (!Number.isSafeInteger(scheduleCompanyId) || scheduleCompanyId < 1) {
            throw new Error("The company identity is required before registering a job schedule");
          }
          schedulerPayload.meta = {
            ...schedulerPayload.meta,
            companyId: scheduleCompanyId,
            jobSeriesKey: `job-series:${scheduleCompanyId}:${targetJobId}`,
          };
          pendingScheduleRef.current = { jobId: targetJobId, payload: schedulerPayload, fromQuote: state.quote !== null };
          if (pendingStoreKey) {
            try {
              localStorage.setItem(`${pendingStoreKey}:schedule`, JSON.stringify(pendingScheduleRef.current));
            } catch (error) {
              console.error("Unable to save pending job schedule locally:", error);
            }
          }
          if (state.quote === null) {
            createdEventResponse = await sendToEventScheduler(schedulerPayload);
          } else {
            createdEventResponse = await createJobEvent(schedulerPayload);
          }
          schedulerRegistrationPending = false;
          pendingScheduleRef.current = null;
          clearPendingRequest();
        }
      }

      if (isUpdateJob) {
        void refetchTodayJobs();
      } else {
        await refetchTodayJobs();
      }

      await handleSuccessUI(targetJobId, {
        isFromScratch: state.quote === null,

        isVisit,

        hasClient: !state.skipClientSelection && Boolean(typeof state.client === "string" ? state.client : state.client?.uid),
      });
      if (!isUpdateJob) clearPendingRequest();

      dispatch(quoteCreateActions.setAlias(""));

      dispatch(setDescription(""));
      dispatch(setNotes([]));
    } catch (error: any) {
      console.error("Error creating or updating job:", error);

      if (schedulerRegistrationPending && schedulerRegistrationJobId) {
        unconfirmedScheduleJobRef.current = schedulerRegistrationJobId;
        setUnconfirmedScheduleJobId(schedulerRegistrationJobId);
        showSnackbar(`Job #${schedulerRegistrationJobId} was created, but its schedule could not be confirmed. Review this job before assigning work.`, "warning");
        return;
      }

      const backendMessage = error?.response?.data?.message;

      showSnackbar(backendMessage || error?.message || (isUpdateJob ? "Error updating job" : "Error creating job"), "error");
    }
  };

  const retryUnconfirmedSchedule = async () => {
    const pending = pendingScheduleRef.current;
    if (!pending || isRetryingSchedule || scheduleRecovered) return;
    setIsRetryingSchedule(true);
    try {
      if (pending.fromQuote) await createJobEvent(pending.payload);
      else await sendToEventScheduler(pending.payload);
      pendingScheduleRef.current = null;
      setScheduleRecovered(true);
      clearPendingRequest();
      void refetchTodayJobs();
      showSnackbar(`The schedule for job #${pending.jobId} is confirmed. Open this job in Jobs to review it.`, "success");
    } catch (error) {
      console.error("Unable to reconcile job schedule:", error);
      showSnackbar(`The schedule for job #${pending.jobId} is still unconfirmed. Try again or review the job in Jobs.`, "warning");
    } finally {
      setIsRetryingSchedule(false);
    }
  };

  const markPendingJobReviewed = () => {
    clearPendingRequest();
    pendingScheduleRef.current = null;
    setReviewedPendingJob(true);
  };

  const isBasic = state.quoteMode === "basic" || state.quote?.type === "basic";

  return {
    state,
    loading: loading || isCreating,
    snackbar,
    closeSnackbar,
    isBasic,
    scopeOfWorkRef,
    handleCreateJob,
    scheduleLater,
    unconfirmedScheduleJobId,
    retryUnconfirmedSchedule,
    isRetryingSchedule,
    scheduleRecovered,
    canRetryUnconfirmedSchedule: Boolean(pendingScheduleRef.current),
    markPendingJobReviewed,
    reviewedPendingJob,
  };
}
